import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 as Trash } from "@/components/icons";
import {
  Button,
  FieldError,
  FieldLabel,
  FormInput,
  RHF,
  useFieldError,
} from "@/components/kit";
import type { ProductPickerItemDTO } from "@/modules/products/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type PickerVariant = ProductPickerItemDTO["variants"][number];

type BundleLineValues = {
  componentVariantId: string;
  quantity: number;
  sortOrder?: number;
  componentProductId?: string;
  componentProductName?: string;
  componentVariantName?: string;
};

type BundleLinesEditorProps = {
  variantIndex: number;
  pickerProducts: ProductPickerItemDTO[];
  pickerReady?: boolean;
  excludeVariantIds?: string[];
};

function isEligibleComponent(
  variant: PickerVariant,
  excludeIds: Set<string>,
): boolean {
  if (variant.variantType === "bundle") return false;
  if (variant.isInventoryOnly) return false;
  if (excludeIds.has(variant.id)) return false;
  return true;
}

function findProductIdForVariant(
  products: ProductPickerItemDTO[],
  variantId: string,
): string {
  if (!variantId) return "";
  for (const product of products) {
    if (product.variants.some((v) => v.id === variantId)) {
      return product.id;
    }
  }
  return "";
}

function productHasEligibleVariants(
  product: ProductPickerItemDTO,
  exclude: Set<string>,
  usedVariantIds: Set<string>,
  currentVariantId?: string,
): boolean {
  return product.variants.some((v) => {
    if (!isEligibleComponent(v, exclude)) return false;
    if (usedVariantIds.has(v.id) && v.id !== currentVariantId) return false;
    return true;
  });
}

function ensureOption(
  options: Array<{ value: string; label: string }>,
  value: string,
  label: string,
) {
  if (!value) return options;
  if (options.some((opt) => opt.value === value)) return options;
  return [{ value, label: label || value }, ...options];
}

function BundleLineRow({
  variantIndex,
  lineIndex,
  pickerProducts,
  pickerReady,
  exclude,
  usedVariantIds,
  onRemove,
}: {
  variantIndex: number;
  lineIndex: number;
  pickerProducts: ProductPickerItemDTO[];
  pickerReady: boolean;
  exclude: Set<string>;
  usedVariantIds: Set<string>;
  onRemove: () => void;
}) {
  const { control, setValue } = RHF.useFormContext();
  const componentError = useFieldError(
    `variants.${variantIndex}.bundleLines.${lineIndex}.componentVariantId`,
  );
  const lineValues = RHF.useWatch({
    control,
    name: `variants.${variantIndex}.bundleLines.${lineIndex}`,
  }) as BundleLineValues | undefined;
  const componentVariantId = lineValues?.componentVariantId ?? "";
  const savedProductId = lineValues?.componentProductId?.trim() ?? "";

  const derivedProductId = useMemo(
    () => findProductIdForVariant(pickerProducts, componentVariantId),
    [pickerProducts, componentVariantId],
  );
  const [manualProductId, setManualProductId] = useState<string | null>(null);

  useEffect(() => {
    setManualProductId(null);
  }, [componentVariantId]);

  const productId =
    manualProductId ?? (derivedProductId || savedProductId || "");

  const productOptions = useMemo(() => {
    const options = pickerProducts
      .filter((product) =>
        productHasEligibleVariants(
          product,
          exclude,
          usedVariantIds,
          componentVariantId,
        ),
      )
      .map((product) => ({
        value: product.id,
        label: product.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (!productId) return options;

    const fromPicker = pickerProducts.find((p) => p.id === productId);
    return ensureOption(
      options,
      productId,
      fromPicker?.name ?? lineValues?.componentProductName ?? productId,
    );
  }, [
    pickerProducts,
    exclude,
    usedVariantIds,
    componentVariantId,
    productId,
    lineValues?.componentProductName,
  ]);

  const selectedProduct = pickerProducts.find((p) => p.id === productId);

  const variantOptions = useMemo(() => {
    const fromProduct = selectedProduct
      ? selectedProduct.variants
          .filter((v) => {
            if (!isEligibleComponent(v, exclude)) return false;
            if (usedVariantIds.has(v.id) && v.id !== componentVariantId) {
              return false;
            }
            return true;
          })
          .map((v) => ({
            value: v.id,
            label: v.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

    if (!componentVariantId) return fromProduct;

    return ensureOption(
      fromProduct,
      componentVariantId,
      lineValues?.componentVariantName ?? componentVariantId,
    );
  }, [
    selectedProduct,
    exclude,
    usedVariantIds,
    componentVariantId,
    lineValues?.componentVariantName,
  ]);

  const variantInOptions = variantOptions.some(
    (opt) => opt.value === componentVariantId,
  );
  const showSavedLabels =
    Boolean(componentVariantId) &&
    pickerReady &&
    !selectedProduct &&
    (lineValues?.componentProductName || lineValues?.componentVariantName);

  function onProductChange(nextProductId: string) {
    setManualProductId(nextProductId);
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentProductId`,
      nextProductId,
      { shouldDirty: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentVariantId`,
      "",
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentProductName`,
      pickerProducts.find((p) => p.id === nextProductId)?.name,
      { shouldDirty: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentVariantName`,
      undefined,
      { shouldDirty: true },
    );
  }

  function onVariantChange(nextVariantId: string) {
    const nextProduct = pickerProducts.find((product) =>
      product.variants.some((variant) => variant.id === nextVariantId),
    );
    const nextVariant = nextProduct?.variants.find(
      (variant) => variant.id === nextVariantId,
    );
    setManualProductId(nextProduct?.id ?? null);
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentVariantId`,
      nextVariantId,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentProductId`,
      nextProduct?.id,
      { shouldDirty: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentProductName`,
      nextProduct?.name,
      { shouldDirty: true },
    );
    setValue(
      `variants.${variantIndex}.bundleLines.${lineIndex}.componentVariantName`,
      nextVariant?.name,
      { shouldDirty: true },
    );
  }

  // Remount selects once picker is ready so Radix gets a controlled value
  // on first paint (avoids blank display after async options load).
  const selectMountKey = pickerReady ? "ready" : "loading";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-secondary/15 bg-background/60 p-3">
      {showSavedLabels ? (
        <p className="text-muted-foreground text-xs">
          ບັນທຶກໄວ້:{" "}
          <span className="font-medium text-foreground">
            {lineValues?.componentProductName ?? "—"}
          </span>
          {" · "}
          <span className="font-medium text-foreground">
            {lineValues?.componentVariantName ?? componentVariantId}
          </span>
        </p>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <FieldLabel className="text-muted-foreground text-xs">ສິນຄ້າ</FieldLabel>
        <SimpleSelect
          key={`product-${selectMountKey}-${lineIndex}`}
          value={productId || undefined}
          onValueChange={onProductChange}
          placeholder={pickerReady ? "ເລືອກສິນຄ້າກ່ອນ" : "ກຳລັງໂຫຼດ..."}
          options={productOptions}
          className="w-full"
          disabled={!pickerReady}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-[1fr_100px_auto] sm:items-end">
        <div className="flex flex-col gap-1.5">
          <FieldLabel className="text-muted-foreground text-xs">
            ຕົວເລືອກ
          </FieldLabel>
          <SimpleSelect
            key={`variant-${selectMountKey}-${lineIndex}-${productId}`}
            value={variantInOptions ? componentVariantId : undefined}
            onValueChange={onVariantChange}
            placeholder={
              !pickerReady
                ? "ກຳລັງໂຫຼດ..."
                : productId
                  ? "ເລືອກຕົວເລືອກ"
                  : "ເລືອກສິນຄ້າກ່ອນ"
            }
            options={variantOptions}
            className="w-full"
            disabled={!pickerReady || !productId || variantOptions.length === 0}
          />
          {pickerReady && productId && variantOptions.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              ສິນຄ້ານີ້ບໍ່ມີຕົວເລືອກທີ່ໃຊ້ເປັນສ່ວນປະກອບໄດ້
            </p>
          ) : null}
          {componentError ? <FieldError>{componentError}</FieldError> : null}
        </div>
        <FormInput
          name={`variants.${variantIndex}.bundleLines.${lineIndex}.quantity`}
          label="ຈຳນວນ"
          type="number"
          min={1}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="self-end text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label="ລຶບລາຍການ"
        >
          <Trash className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export function BundleLinesEditor({
  variantIndex,
  pickerProducts,
  pickerReady = true,
  excludeVariantIds = [],
}: BundleLinesEditorProps) {
  const { control, getValues } = RHF.useFormContext();
  const bundleLinesError = useFieldError(
    `variants.${variantIndex}.bundleLines`,
  );
  const { fields, append, remove, replace } = RHF.useFieldArray({
    control,
    name: `variants.${variantIndex}.bundleLines`,
    keyName: "fieldId",
  });

  // Nested useFieldArray can miss defaultValues on first mount — re-sync once.
  useEffect(() => {
    if (fields.length > 0) return;
    const lines = getValues(`variants.${variantIndex}.bundleLines`) as
      | BundleLineValues[]
      | undefined;
    if (Array.isArray(lines) && lines.length > 0) {
      replace(lines);
    }
  }, [fields.length, getValues, replace, variantIndex]);

  const currentVariantId = RHF.useWatch({
    control,
    name: `variants.${variantIndex}.id`,
  }) as string | undefined;

  const bundleLines = RHF.useWatch({
    control,
    name: `variants.${variantIndex}.bundleLines`,
  }) as BundleLineValues[] | undefined;

  const exclude = useMemo(
    () =>
      new Set([
        ...excludeVariantIds,
        ...(currentVariantId ? [currentVariantId] : []),
      ]),
    [excludeVariantIds, currentVariantId],
  );

  const hasAnyEligible = useMemo(
    () =>
      pickerProducts.some((product) =>
        product.variants.some((v) => isEligibleComponent(v, exclude)),
      ),
    [pickerProducts, exclude],
  );

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-leaf/25 bg-leaf/4 p-4">
      <div className="flex items-center justify-between gap-2">
        <FieldLabel>ສ່ວນປະກອບຕໍ່ 1 ຊຸດ</FieldLabel>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() =>
            append({
              componentVariantId: "",
              quantity: 1,
              sortOrder: undefined,
            } satisfies BundleLineValues)
          }
          disabled={!pickerReady || !hasAnyEligible}
        >
          <Plus data-icon="inline-start" />
          ເພີ່ມລາຍການ
        </Button>
      </div>

      {bundleLinesError ? <FieldError>{bundleLinesError}</FieldError> : null}

      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          ເພີ່ມສ່ວນປະກອບຢ່າງນ້ອຍ 1 ລາຍການ — ເລືອກສິນຄ້າກ່ອນ ແລ້ວເລືອກຕົວເລືອກ
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {fields.map((field, lineIndex) => {
            const usedVariantIds = new Set<string>();
            for (const [idx, line] of (bundleLines ?? []).entries()) {
              if (idx === lineIndex) continue;
              if (line?.componentVariantId?.trim()) {
                usedVariantIds.add(line.componentVariantId);
              }
            }

            return (
              <li key={field.fieldId}>
                <BundleLineRow
                  variantIndex={variantIndex}
                  lineIndex={lineIndex}
                  pickerProducts={pickerProducts}
                  pickerReady={pickerReady}
                  exclude={exclude}
                  usedVariantIds={usedVariantIds}
                  onRemove={() => remove(lineIndex)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
